import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CssBaseline, ThemeProvider, createTheme } from "@mui/material";
import "./tokens.css";

const STORAGE_KEY = "theme"; // "light" | "dark"

const getSystemPrefersDark = () => {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

// Determine initial theme mode and whether the user has explicitly chosen one.
function readInitialThemeState() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "light" || saved === "dark") {
      return { mode: saved, hasUserPref: true };
    }
  } catch {}
  // No saved preference: follow system, do not persist yet
  return { mode: getSystemPrefersDark() ? "dark" : "light", hasUserPref: false };
}

const ThemeModeContext = createContext({ mode: "light", toggleMode: () => {} });

export const useThemeMode = () => useContext(ThemeModeContext);

function readCssVarTokens() {
  if (typeof document === "undefined") return {};
  try {
    const styles = getComputedStyle(document.documentElement);
    const read = (name) => (styles.getPropertyValue(name) || "").trim() || undefined;
    return {
      primary: read("--primary"),
      onPrimary: read("--on-primary"),
      bg: read("--bg"),
      bgElevated: read("--bg-elevated"),
      text: read("--text"),
      textMuted: read("--text-muted"),
      divider: read("--divider"),
      success: read("--success"),
      warning: read("--warning"),
      error: read("--error"),
      info: read("--info"),
    };
  } catch {
    return {};
  }
}

export default function AppThemeProvider({ children }) {
  const [{ mode, hasUserPref }, setThemeState] = useState(() => readInitialThemeState());

  // Apply data-theme to <html> and persist only if user explicitly chose a preference
  useEffect(() => {
    try {
      if (typeof document !== "undefined") {
        document.documentElement.setAttribute("data-theme", mode);
      }
      if (hasUserPref) {
        localStorage.setItem(STORAGE_KEY, mode);
      } else {
        // Ensure no stale value if user hasn't chosen yet
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {}
  }, [mode, hasUserPref]);

  // Keep in sync with changes to system preference until user picks a theme
  useEffect(() => {
    const mql = window.matchMedia?.("(prefers-color-scheme: dark)");
    if (!mql) return;
    const handler = (e) => {
      setThemeState((prev) => {
        if (prev.hasUserPref) return prev; // don't auto-switch after user preference
        const next = e.matches ? "dark" : "light";
        if (typeof document !== "undefined") {
          document.documentElement.setAttribute("data-theme", next);
        }
        return { ...prev, mode: next };
      });
    };
    mql.addEventListener?.("change", handler);
    return () => mql.removeEventListener?.("change", handler);
  }, []);

  const toggleMode = useCallback(() => {
    setThemeState((prev) => {
      const next = prev.mode === "light" ? "dark" : "light";
      // Persist synchronously to mark explicit user choice
      try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      if (typeof document !== "undefined") {
        document.documentElement.setAttribute("data-theme", next);
      }
      return { mode: next, hasUserPref: true };
    });
  }, []);

  const theme = useMemo(() => {
    const t = readCssVarTokens();
    const palette = { mode };
    if (t.primary) palette.primary = { main: t.primary, contrastText: t.onPrimary };
    if (t.bg || t.bgElevated) palette.background = { default: t.bg || undefined, paper: t.bgElevated || t.bg };
    if (t.text || t.textMuted) palette.text = { primary: t.text || undefined, secondary: t.textMuted || undefined };
    if (t.divider) palette.divider = t.divider;
    if (t.success) palette.success = { main: t.success };
    if (t.warning) palette.warning = { main: t.warning };
    if (t.error) palette.error = { main: t.error };
    if (t.info) palette.info = { main: t.info };

    return createTheme({
      palette,
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: "var(--bg)",
              color: "var(--text)",
            }
          }
        }
      }
    });
  }, [mode]);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}
